import $ from 'jquery';
import { debounce, forEach } from 'underscore';
import shave from 'shave';
import EventEmitter from 'eventemitter3';
import Glide from '@glidejs/glide/dist/glide.esm';

// Pull in the iframe-resizer child script so it can pair with the parent page
import '@iframe-resizer/child';

import Carousel from './util/carousel';
import checkIfVisible from './util/checkIfVisible';

const AnalyticEvents = {
  CAROUSEL: 'RW_CAROUSEL',
  INCENTIVIZED_TOOLTIP_TOGGLE: 'RW_INCENTIVIZED_TOOLTIP_TOGGLE',
  MEDIA_VIEW: 'RW_RECO_MEDIA_VIEW',
  MEDIA_CLOSE: 'RW_RECO_MEDIA_CLOSE',
  MOUNT: 'RW_MOUNT',
  RECOMMENDATION_CLICK: 'RW_RECO_CLICK',
  RECOMMENDATION_VIEW: 'RW_RECO_VIEW',
  RECOMMENDATION_VOTE: 'RW_RECO_VOTE',
  RECOMMENDATION_VOTE_ERROR: 'RW_RECO_VOTE_ERROR',
  READ_MORE_TOGGLE: 'RW_RECO_READ_MORE',
  SHOW_MORE_CLICK: 'RW_SHOW_MORE',
  TOOLTIP_TOGGLE: 'RW_TOOLTIP_TOGGLE',
  VIEWED: 'RW_VIEW',
};

// Expose jQuery on the window
window.$ = $;
window.jQuery = $;

// Expose the analytics collector on the window
const analytics = require('exp-analytics-collector');

// Modify the analytics.sendEvent to include the base data exposed on window.acData
const baseSendEvent = analytics.sendEvent;
analytics.sendEvent = (event, data = {}, ...args) => {
  // Prepend the event name w/ PREVIEW_ if the widget is in preview mode
  const evt = window.preview ? `${event}_PREVIEW` : event;
  baseSendEvent.call(analytics, evt, { ...data, ...window.acData || {} }, ...args);
};

window.analytics = analytics;

// Utility function for initializing the Read More behavior
const initReadMore = () => {
  const $recoWithoutReadMore = $('.ev-recommendation:not(.ev-read-more):first').first();
  if (!$recoWithoutReadMore.length) {
    return;
  }

  const detailsMaxHeight = $recoWithoutReadMore.find('.ev-recommendation-details')
    .first().height();

  // Only apply the options if a max-height for a details object can be determined
  if (detailsMaxHeight && detailsMaxHeight > 0) {
    const titleHeight = $recoWithoutReadMore.find('.ev-recommendation-title:first').first().height() || 0;
    const readMoreToggle = $('.ev-recommendation-body-toggle').clone().wrap('<div />').parent()
      .html();

    const shaveOptions = {
      classname: 'ev-recommendation-body-overflow',
      character: `&hellip; ${readMoreToggle}`,
    };

    shave('.ev-recommendation:not(.ev-read-more) .ev-recommendation-body-text', detailsMaxHeight, shaveOptions);

    if (titleHeight) {
      // Shave the title height off of any recommendation bodies
      const bodyWithTitleMaxHeight = detailsMaxHeight - titleHeight;
      shave('.ev-recommendation:not(.ev-read-more) .ev-recommendation-title ~ .ev-recommendation-body .ev-recommendation-body-text', bodyWithTitleMaxHeight, shaveOptions);
    }

    // Add an aria-hidden="true" to the js-shave-char to hide it from screen readers
    $('.js-shave-char').attr('aria-hidden', 'true');

    // Override the display: none on the overflow since it's managed by other CSS
    $('.ev-recommendation-body-overflow').show();
  }
};

const updateMediaViewerPosition = (recommendationId) => {
  const $mediaViewer = $('.ev-media-viewer');
  if ($(window).width() > 598) {
    $mediaViewer.css({ top: '' });
    return false;
  }

  let id = recommendationId;
  if (!id) {
    const $shown = $('.ev-media-viewer.show');
    if (!$shown.length) return false;
    id = $shown.data('recommendationId');
  }

  const offsetY = $(`.ev-recommendation[data-recommendation-id="${id}"]`)
    .offset().top;
  $mediaViewer.offset({ top: offsetY });
  return true;
};

const updateMediaViewerItemNumber = (index = 0) => {
  const $el = $('.ev-media-viewer.show .item-number');
  const total = $el.data('totalItems');
  $el.text(`${index + 1}/${total}`);
};

const getIncentivizedTooltipFromTrigger = (triggerEl) => {
  const recommendationId = $(triggerEl).closest('.ev-recommendation')
    .data('recommendation-id');
  return $(`#ev-incentivized-tooltip-${recommendationId}`);
};

/**
 * Recommendations Display Widget Internal JS Functionality
 *
 * @author jon.morris
 */
class Recommendations extends EventEmitter {
  static bubbleMessageUp(event, data = {}) {
    if (window.parent && window.parent.postMessage) {
      const message = `[EV]${JSON.stringify({ event, data })}`;
      window.parent.postMessage(message, '*');
    }
  }

  constructor() {
    super();

    // Create the Carousel utility and bind to fire an analytic event on its 'shift' event
    this.carousel = new Carousel();
    this.carousel.on('shift', (data) => analytics.sendEvent(AnalyticEvents.CAROUSEL, data));
  }

  bind() {
    // Bind a click listener to hide the tooltip if the click target was inside the tooltip
    $(document).on('click', (e) => {
      const { target } = e;
      const $target = $(target);
      if (this.tooltipVisible) {
        const isWidgetTooltipTrigger = $target.is('.ev-tooltip-trigger');
        if (!isWidgetTooltipTrigger) {
          const $widgetTooltip = $target.closest('#ev-widget-tooltip');
          if (!$widgetTooltip.length) {
            e.preventDefault();
            this.toggleWidgetTooltip(false, 'outer-click');
          }
        }

        const isIncentivizedTooltipTrigger = $target.is('.ev-incentivized-tooltip-trigger');
        if (!isIncentivizedTooltipTrigger) {
          const $incentivizedTooltip = $target.closest('.ev-incentivized-tooltip');
          if (!$incentivizedTooltip.length) {
            e.preventDefault();
            this.closeIncentivizedTooltip('outer-click');
          }
        }
      }
    });

    const cancelingClickHandler = (e, fn) => {
      e.preventDefault();
      e.stopPropagation();
      fn();
      return false;
    };

    // Handle tooltips
    $('.ev-tooltip-trigger').on('click', (e) => cancelingClickHandler(e, () => {
      this.closeIncentivizedTooltip('tooltip');
      this.toggleWidgetTooltip();
    }));

    $('.ev-incentivized-tooltip-trigger')
      .on('click', (e) => cancelingClickHandler(e, () => {
        const $tooltip = getIncentivizedTooltipFromTrigger(e.currentTarget);
        this.toggleWidgetTooltip(false, 'tooltip');
        this.closeIncentivizedTooltip('tooltip', $tooltip);
        const showTooltip = $tooltip.data('trigger') !== 'click';
        this.toggleIncentivizedTooltip($tooltip, showTooltip);
      }))
      .on('mouseenter', (e) => cancelingClickHandler(e, () => {
        const $tooltip = getIncentivizedTooltipFromTrigger(e.currentTarget);
        if ($tooltip.data('trigger') !== 'click') {
          this.toggleIncentivizedTooltip($tooltip, true, 'hover');
        }
      }))
      .on('mouseleave', (e) => cancelingClickHandler(e, () => {
        const $tooltip = getIncentivizedTooltipFromTrigger(e.currentTarget);
        if ($tooltip.data('trigger') !== 'click') {
          this.toggleIncentivizedTooltip($tooltip, false, 'hover');
        }
      }));

    // Bind on the show more trigger to do just that
    $('#ev-show-more-trigger').on('click', (e) => cancelingClickHandler(e, () => this.showMoreRecommendations()));

    // Bind to the vote click event to fire off the vote
    $('.ev-recommendation-vote:not([data-voting="true"])').on('click', (e) => cancelingClickHandler(e, () => {
      const $target = $(e.currentTarget);
      const recommendationId = $target.closest('.ev-recommendation')
        .data('recommendation-id');
      const isUp = $target.is('.ev-recommendation-vote-up');

      this.voteOnRecommendation(recommendationId, isUp);
    }));

    // Bind to the Read More click to expand the recommendation body
    $('.ev-recommendation')
      .on('click', '.ev-recommendation-body-toggle', (e) => {
        cancelingClickHandler(e, () => {
          const $target = $(e.currentTarget);
          const $recommendation = $target.closest('.ev-recommendation');
          $recommendation
            .addClass('ev-read-more')
            .attr('aria-expanded', 'true');

          const index = $recommendation.data('index');
          const recommendationId = $recommendation.data('recommendation-id');

          this.emit('readMore', { index, recommendationId });

          analytics.sendEvent(AnalyticEvents.READ_MORE_TOGGLE, {
            index,
            recommendationId,
          });
        });
      })

      // Bind to media thumbnail click to show media viewer modal
      .on('click', '.ev-recommendation-media-thumbnail', (e) => {
        cancelingClickHandler(e, () => {
          const $target = $(e.currentTarget);
          const $recommendation = $target.closest('.ev-recommendation');
          const recommendationId = $recommendation.data('recommendation-id');

          this.showMediaViewerModal(recommendationId);
        });
      })

      // Bind a click listener to the recommendation elements
      .on('click', (e) => {
        const $recommendation = $(e.currentTarget);

        // Pick out the ac-target of what was clicked
        const acTarget = $(e.target).closest('[data-ac-target]').data('acTarget');

        const index = $recommendation.data('index');
        const recommendationId = $recommendation.data('recommendation-id');

        analytics.sendEvent(AnalyticEvents.RECOMMENDATION_CLICK, {
          index,
          recommendationId,
          target: acTarget,
        });
      })

      // Bind a listener to the recommendations so that they get moved into view on click or focus
      .on('click focusin', (e) => {
        const $recommendation = $(e.currentTarget);

        const recommendationId = $recommendation.data('recommendation-id');
        this.carousel.shiftToRecommendation(recommendationId);
      });

    $('.ev-media-viewer').on('click', '.ev-media-viewer-close', (e) => {
      cancelingClickHandler(e, () => {
        this.closeMediaViewerModal();
      });
    });

    $('.ev-media-viewer-container').on('click', (e) => {
      cancelingClickHandler(e, () => {
        if ($(e.target).closest('.ev-media-viewer').length) return;

        this.closeMediaViewerModal();
      });
    });

    // Bind a scroll listener to the recommendations container to kill scroll events when needed
    $('#ev-recommendations').on('scroll', (e) => {
      $(e.currentTarget).scrollLeft(0);
    });

    // Bind a click listener to the recommendation list carousel action elements
    $('.ev-recommendation-carousel-action.ev-carousel-previous').on('click', (e) => cancelingClickHandler(e, () => {
      this.closeMediaViewerModal();
      this.carousel.shift(false);
    }));

    $('.ev-recommendation-carousel-action.ev-carousel-next').on('click', (e) => cancelingClickHandler(e, () => {
      this.closeMediaViewerModal();
      this.carousel.shift(true);
    }));

    // Bind a keyup listener to manage key events
    $(document).on('keyup', (e) => this.onKeyUp(e));

    // Bind a debounced resize listener to manage resize events
    $(window).on('resize', debounce(() => this.onResize(), 50));

    // Bind a scroll listener to the recommendation list container & the document
    // to fire a recommendation view event whenever a recommendation is viewed in the viewport.
    const checkForAndHandleViewedRecommendations = debounce((parentRect = null) => {
      if (!this.viewed) {
        // Ignore the calculation if the widget has yet to be viewed.
        return;
      }

      $('.ev-recommendation').each((index, reco) => {
        const $reco = $(reco);
        if (!$reco.is('[data-viewed="true"]') && checkIfVisible(reco, parentRect)) {
          $reco.attr('data-viewed', 'true');

          const recommendationId = $reco.data('recommendationId');
          analytics.sendEvent(AnalyticEvents.RECOMMENDATION_VIEW, { index, recommendationId });
          this.emit('recommendationView', { index, recommendationId });
        }
      });
    }, 50);

    $('.ev-recommendation-list').on('scroll', checkForAndHandleViewedRecommendations);
    $(document).on('scroll', checkForAndHandleViewedRecommendations);
    checkForAndHandleViewedRecommendations();

    // Bind up to any messages published from the parent to the window.postMessage
    const eventMethod = window.addEventListener ? 'addEventListener' : 'attachEvent';
    const messageEvent = eventMethod === 'attachEvent' ? 'onmessage' : 'message';
    window[eventMethod](messageEvent, (e) => this.onMessageFromParent(e));

    // Bind on all events and bubble them up to the parent window
    forEach(['incentivizedTooltip', 'recommendationView', 'readMore', 'showMore', 'tooltip', 'viewMedia', 'vote'], (event) => {
      this.on(event, (data) => Recommendations.bubbleMessageUp(event, data));
    });

    // Bind on some events triggered by the parent JS plugin
    this
      .on('mounted', (data = {}) => {
        window.acData = {
          ...window.acData,
          ...data,
        };

        analytics.sendEvent(AnalyticEvents.MOUNT);
      })
      .on('parentClick', () => {
        this.closeAllTooltips('parentClick');
      })
      .on('parentScroll', (parentRect) => {
        checkForAndHandleViewedRecommendations(parentRect);
      })
      .on('view', () => {
        this.viewed = true;
        analytics.sendEvent(AnalyticEvents.VIEWED);
        checkForAndHandleViewedRecommendations();
      });
  }

  init() {
    // Bind up on all of the necessary events
    this.bind();

    // Initialize the Carousel and Read More functionality
    this.carousel.init();
    initReadMore();

    // Initialize flag to mark if any tooltips are currently shown
    this.tooltipVisible = false;
  }

  onKeyUp(e) {
    const key = e.key || e.keyCode || e.which;

    // Close the tooltip if an escape was keyed
    if (key === 'Escape' || key === 'Esc' || key === 27) {
      this.toggleWidgetTooltip(false, 'esc');
      this.closeMediaViewerModal();
      return;
    }

    // Shift the recommendation carousel if a left or right was keyed
    if (key === 'ArrowLeft' || key === 'Left' || key === 37) {
      if (!this.mediaViewerVisible) this.carousel.shift(false);
    } else if (key === 'ArrowRight' || key === 'Right' || key === 39) {
      if (!this.mediaViewerVisible) this.carousel.shift(true);
    }
  }

  onMessageFromParent(event) {
    if (!event?.data?.startsWith?.('[EV]')) {
      // Ignore the event since it is not part of the recommendations widget functionality
      return;
    }

    // Parse out the JSON data from the event
    let eventData = {};
    try {
      eventData = JSON.parse(event.data.substr(4));
    } catch {
      // Ignore error
    }

    // Emit the event from the child window
    if (eventData.event) {
      this.emit(eventData.event, eventData.data);
    }
  }

  onResize() {
    this.carousel.updateVisibility();
    initReadMore();
    updateMediaViewerPosition();
  }

  showMoreRecommendations() {
    const scrollTop = $(window).scrollTop();

    // Find the first focusable element with the first non-visible recommendation
    const $firstNotVisible = $('.ev-recommendation:not(:visible):first');

    // Add the ev-show-more class and evaluate the Read More on the newly visible recommendations
    $('#ev-recommendations').addClass('ev-show-more');
    initReadMore();

    // Focus the first focusable element to preserve the tab order
    $firstNotVisible.find('button, a[href]').first()
      .focus();

    // Reset the scroll top so the scroll doesn't shift
    $(window).scrollTop(scrollTop);

    analytics.sendEvent(AnalyticEvents.SHOW_MORE_CLICK);
    this.emit('showMore');
  }

  toggleTooltip($tooltip, toggle, trigger) {
    // If a tooltip element isn't passed, then skip the toggle checks.
    if (!$tooltip.length) {
      return {
        changed: false,
        visible: false,
      };
    }
    const previouslyVisible = !$tooltip.hasClass('hidden');

    let visible;
    if (toggle || ($tooltip.hasClass('hidden') && toggle === undefined)) {
      visible = true;
      $tooltip.removeClass('hidden');
      $tooltip.data('trigger', trigger);
      $tooltip.show(); // Show to get rid of the inline style hiding it
    } else if (toggle === undefined || !toggle) {
      visible = false;
      $tooltip.addClass('hidden');
      $tooltip.removeData('trigger');
    }
    this.tooltipVisible = visible;

    return {
      changed: previouslyVisible !== visible,
      visible,
    };
  }

  toggleWidgetTooltip(toggle, trigger = 'click') {
    const $tooltip = $('#ev-widget-tooltip');
    const { changed, visible } = this.toggleTooltip($tooltip, toggle, trigger);

    if (changed) {
      analytics.sendEvent(AnalyticEvents.TOOLTIP_TOGGLE, { trigger, visible });
      this.emit('tooltip', { visible });
    }
  }

  toggleIncentivizedTooltip($tooltip, toggle, trigger = 'click') {
    const { changed, visible } = this.toggleTooltip($tooltip, toggle, trigger);

    if (changed) {
      const $recommendation = $tooltip.closest('.ev-recommendation');
      const index = $recommendation.data('index');
      const recommendationId = $recommendation.data('recommendation-id');

      analytics.sendEvent(AnalyticEvents.INCENTIVIZED_TOOLTIP_TOGGLE, {
        index,
        recommendationId,
        trigger,
        visible,
      });
      this.emit('incentivizedTooltip', {
        index,
        recommendationId,
        visible,
      });
    }
  }

  closeIncentivizedTooltip(trigger, $excludeTarget) {
    const $tooltip = $('.ev-incentivized-tooltip:not(.hidden)');
    if ($tooltip.length) {
      $tooltip.each((i, el) => {
        if ($excludeTarget && $excludeTarget[0] === el) return;
        this.toggleIncentivizedTooltip($(el), false, trigger);
      });
    }
  }

  closeAllTooltips(trigger) {
    this.toggleWidgetTooltip(false, trigger);
    this.closeIncentivizedTooltip(trigger);
  }

  showMediaViewerModal(recommendationId) {
    const $modal = $(`.ev-media-viewer[data-recommendation-id="${recommendationId}"]`);
    if (!$modal.length) return;

    this.closeAllTooltips('media-modal');
    updateMediaViewerPosition(recommendationId);
    $modal.addClass('show');
    updateMediaViewerItemNumber();

    $('.ev-media-viewer-container').fadeIn(300, () => {
      if ($modal.hasClass('glide')) {
        this.initMediaSlideshow(recommendationId)
          .then(() => this.initVideoPlayers(recommendationId));
      } else {
        this.initVideoPlayers(recommendationId);
      }
      this.mediaViewerVisible = true;
    });

    const index = $modal.data('index');

    this.emit('viewMedia', { index, recommendationId, expanded: true });
    analytics.sendEvent(AnalyticEvents.MEDIA_VIEW, {
      index,
      recommendationId,
      expanded: true,
    });
  }

  closeMediaViewerModal() {
    const $container = $('.ev-media-viewer-container');
    if (!$container.is(':visible')) return;

    $container.fadeOut(300);
    setTimeout(() => {
      this.cleanUpMediaSlideshow();
      this.cleanUpVideoPlayers();
      const $mediaViewer = $('.ev-media-viewer.show');
      $mediaViewer.removeClass('show');
      this.mediaViewerVisible = false;

      const recommendationId = $mediaViewer.data('recommendation-id');
      if (recommendationId) {
        const index = $mediaViewer.data('index');

        this.emit('viewMedia', { index, recommendationId, expanded: false });
        analytics.sendEvent(AnalyticEvents.MEDIA_CLOSE, {
          index,
          recommendationId,
          expanded: false,
        });
      }
    }, 300);
  }

  initMediaSlideshow(recommendationId, startingIndex = 0) {
    return new Promise((resolve) => {
      this.glide = new Glide(`.ev-media-viewer[data-recommendation-id="${recommendationId}"]`, {
        type: 'carousel',
        startAt: startingIndex,
        perView: 1,
        dragThreshold: false,
      });

      this.glide.on('mount.after', () => resolve());

      this.glide.on('run', () => this.pauseAllVideos());

      this.glide.on('run.after', () => {
        updateMediaViewerItemNumber(this.glide.index);
      });

      this.glide.mount();
    });
  }

  cleanUpMediaSlideshow() {
    if (this.glide) this.glide.destroy();
  }

  initVideoPlayers(recommendationId) {
    if (!window.videojs) return;

    const $videos = $(`.ev-media-viewer[data-recommendation-id="${recommendationId}"] .glide__slide:not(.glide__slide--clone) video`);
    this.videoPlayerIds = $videos.map((index, el) => {
      // If video player was previously initialized, skip init and return id
      if (!$(el).hasClass('video-js')) return $(el).parent().get(0).id;

      const player = window.videojs(el, { controls: true });
      return player.id();
    }).get();
  }

  pauseAllVideos() {
    if (!window.videojs) return;
    this.videoPlayerIds.forEach((id) => {
      const player = window.videojs(id);
      if (!player.paused()) player.pause();
    });
  }

  cleanUpVideoPlayers() {
    if (!this.videoPlayerIds || !window.videojs) return;
    // Resets each video player to "unplayed" state (we cannot dispose of the
    // player since videojs removes video data from DOM on disposal)
    this.videoPlayerIds.forEach((id) => {
      const player = window.videojs(id);
      if (!player.paused()) player.pause();
      player.currentTime(0);
      player.hasStarted(false);
    });
  }

  /**
   * Vote on a Recommendation
   *
   * @param recommendationId
   * @param isUp
   */
  voteOnRecommendation(recommendationId, isUp) {
    const $recommendation = $(`#ev-recommendations [data-recommendation-id="${recommendationId}"]`);

    // Check if voting is already happening
    if ($recommendation.data('voting')) {
      return;
    }

    // Set the data-voting attribute so the click handler ignores it
    $recommendation
      .data('voting', true)
      .attr('data-voting', 'true');

    const direction = isUp ? 'UP' : 'DOWN';

    const index = $recommendation.data('index');
    const existingVote = $recommendation.attr('data-vote');
    const isVoteDelete = existingVote && existingVote === direction;

    const $up = $recommendation.find('.ev-recommendation-vote-up');
    const $down = $recommendation.find('.ev-recommendation-vote-down');

    const $upCount = $up.find('.ev-recommendation-vote-count');
    const $downCount = $down.find('.ev-recommendation-vote-count');

    let upCount = parseInt(($upCount && $upCount.text()) || '0', 10);
    let downCount = parseInt(($downCount && $downCount.text()) || '0', 10);

    let promise = $.when();
    if (existingVote && existingVote === direction) {
      // Remove the vote since it matches the existing vote
      if (isUp) {
        upCount -= 1;
      } else {
        downCount -= 1;
      }

      // Fire off the delete request if it's not preview mode
      if (!window.preview) {
        promise = $.ajax({
          url: `/widget/recommendations/vote/${recommendationId}`,
          method: 'DELETE',
        });
      }
    } else {
      // Add the vote for the appropriate direction
      if (isUp) {
        upCount += 1;
        if (existingVote) downCount -= 1;
      } else {
        downCount += 1;
        if (existingVote) upCount -= 1;
      }

      // Fire off the save request if it's not preview mode
      if (!window.preview) {
        promise = $.ajax({
          url: `/widget/recommendations/vote/${recommendationId}/${direction}`,
          method: 'POST',
        });
      }
    }

    // Toggle the votes regardless of whether or not the persistence request is successful
    $upCount.text(Math.max(upCount, 0));
    $downCount.text(Math.max(downCount, 0));

    // Set the vote direction data attribute
    $recommendation.attr('data-vote', isVoteDelete ? '' : direction);

    // Update the aria-pressed attributes
    $up.attr('aria-pressed', isVoteDelete ? 'false' : isUp);
    $down.attr('aria-pressed', isVoteDelete ? 'false' : !isUp);

    analytics.sendEvent(AnalyticEvents.RECOMMENDATION_VOTE, {
      changed: !!existingVote,
      deleted: isVoteDelete,
      direction,
      index,
      recommendationId,
    });

    // Emit a 'vote' event
    this.emit('vote', {
      changed: !!existingVote,
      deleted: isVoteDelete,
      direction,
      recommendationId,
    });

    // Fire an error analytic event if the vote save request fails
    promise
      .catch(() => {
        analytics.sendEvent(AnalyticEvents.RECOMMENDATION_VOTE_ERROR, {
          changed: !!existingVote,
          deleted: isVoteDelete,
          recommendationId,
        });
      })
      .always(() => {
        // Clear out the voting data attribute so other votes can happen
        $recommendation
          .data('voting', false)
          .attr('data-voting', '');
      });
  }
}

// Build up the Exp.Recommendations object & expose it to the window
window.Exp = window.Exp || {};
window.Exp.Recommendations = new Recommendations();

$(() => {
  window.Exp.Recommendations.init();

  // Bubble up a 'load' event with the ac data so it is available in the parent window
  Recommendations.bubbleMessageUp('ready', window.acData);
});
